import type { StandardServicePageContent } from "~/content/servicePage";

export const ElectricalSafetyCheckContent: StandardServicePageContent = {
  heroTitle: "Przegląd instalacji elektrycznej",
  quickFactsTitle: "Najważniejsze informacje w skrócie",
  quickFacts: [
    {
      label: "Jak często",
      value: "Minimum co 5 lat",
      description:
        "Obowiązek wynika z art. 62 ust. 1 pkt 2 Prawa budowlanego. Wcześniej warto działać po remoncie, zalaniu, przepięciu albo przy objawach awarii, zanim problem przerodzi się w kosztowną naprawę.",
    },
    {
      label: "Ile kosztuje",
      value: "Zwykle 200-400 zł za mieszkanie",
      description:
        "Dla domu jednorodzinnego najczęściej 300-600 zł. Cena zależy od liczby obwodów, zakresu pomiarów, metrażu oraz miasta, w którym szukasz wykonawcy.",
    },
    {
      label: "Co obejmuje",
      value: "Oględziny, pomiary i protokół",
      description:
        "Najczęściej sprawdza się stan rozdzielnicy, rezystancję izolacji, ciągłość PE, impedancję pętli zwarcia i działanie RCD. Dobry protokół zawiera też wnioski i zalecenia dalszych prac.",
    },
    {
      label: "Kiedy działać wcześniej",
      value: "Po awarii, zakupie starej nieruchomości lub rozbudowie instalacji",
      description:
        "Także po montażu pompy ciepła, ładowarki EV, fotowoltaiki albo gdy zabezpieczenia wybijają bez jasnej przyczyny. To częsty krok przed wynajmem lub zakupem starszego lokalu.",
    },
  ],
  faqTitle: "Najczęstsze pytania o przegląd instalacji elektrycznej",
  sections: [
    {
      title: "Na czym polega przegląd instalacji elektrycznej i co obejmuje",
      paragraphs: [
        `Przegląd instalacji elektrycznej to kontrola bezpieczeństwa instalacji w budynku. Obejmuje oględziny oraz pomiary, które pokazują, czy przewody, zabezpieczenia, rozdzielnica i układ ochrony przeciwporażeniowej działają prawidłowo.`,
        `W praktyce chodzi o wykrycie usterek, których nie widać na co dzień: zużytej izolacji, przegrzewających się połączeń, źle dobranych zabezpieczeń albo błędów po wcześniejszych przeróbkach. To właśnie dlatego sam szybki ogląd gniazd i tablicy nie wystarcza, jeśli chcesz realnie ocenić stan instalacji.`,
        `Po kontroli właściciel dostaje protokół z wynikiem i zaleceniami. W dobrym dokumencie powinny znaleźć się nie tylko same pomiary, ale też informacja, czy instalacja nadaje się do dalszego użytkowania oraz jakie elementy wymagają naprawy, dalszej diagnostyki albo modernizacji.`,
      ],
    },
    {
      title: "Obowiązkowy przegląd instalacji elektrycznej co 5 lat i kiedy zrobić go wcześniej",
      paragraphs: [
        `Okresowy przegląd instalacji elektrycznej jest obowiązkowy co najmniej raz na 5 lat. Wynika to z art. 62 ust. 1 pkt 2 Prawa budowlanego i dotyczy właściciela albo zarządcy nieruchomości.`,
        `Ten termin warto traktować jako minimum. Kontrolę dobrze zlecić wcześniej po remoncie, zalaniu, przepięciu, pożarze, rozbudowie instalacji albo wtedy, gdy pojawiają się objawy takie jak wybijające zabezpieczenia, miganie oświetlenia czy nagrzewanie osprzętu.`,
        `Aktualny protokół ma znaczenie także poza samym obowiązkiem ustawowym. Przydaje się przy wynajmie, sprzedaży nieruchomości, porządkowaniu dokumentacji technicznej i w rozmowach z ubezpieczycielem, który po szkodzie często pyta o potwierdzenie wcześniejszych kontroli.`,
      ],
    },
    {
      title: "Przegląd instalacji elektrycznej w domu i mieszkaniu",
      paragraphs: [
        `W domu jednorodzinnym zakres kontroli bywa szerszy niż w mieszkaniu, bo dochodzą obwody zewnętrzne, garaż, kotłownia, brama, pompa ciepła czy fotowoltaika. W mieszkaniu większy nacisk kładzie się zwykle na rozdzielnicę, gniazda, obwody oświetlenia i skuteczność ochrony przeciwporażeniowej.`,
        `Niezależnie od typu nieruchomości celem jest jedna odpowiedź: czy instalacja nadaje się do dalszego bezpiecznego użytkowania i czy wymaga naprawy albo modernizacji. To szczególnie ważne po zakupie starszego lokalu, przed wynajmem i po większych zmianach w instalacji.`,
        `W praktyce w starszych mieszkaniach i domach najczęściej wychodzą problemy z przewodami aluminiowymi, brakiem RCD, nieczytelnymi oznaczeniami obwodów albo rozbudową wykonywaną etapami przez lata. W dużych miastach, gdzie jest dużo starszych kamienic i bloków z poprzednich dekad, takie przypadki są szczególnie częste i dlatego zakres oględzin powinien być dopasowany do wieku budynku oraz historii przeróbek.`,
      ],
    },
    {
      title: "Cena przeglądu instalacji elektrycznej",
      paragraphs: [
        `Cena przeglądu instalacji elektrycznej zależy od rodzaju obiektu, liczby obwodów i zakresu pomiarów. W mieszkaniu koszt wynosi zwykle 200-400 zł, a w domu jednorodzinnym najczęściej 300-600 zł.`,
        `Na wycenę wpływa też lokalny rynek. W większych miastach cena bywa wyższa ze względu na dostępność terminów, koszty dojazdu i większą liczbę obwodów w nowszych, rozbudowanych instalacjach, ale równie ważne jest to, co dokładnie zawiera oferta.`,
        `Porównując oferty, warto sprawdzić, czy cena obejmuje realne pomiary, ocenę rozdzielnicy i protokół z wnioskami. Sama najniższa stawka bez jasno określonego zakresu zwykle niewiele mówi o jakości usługi, a później kończy się dopłatami za pomiary albo za sam dokument końcowy.`,
      ],
    },
  ],
  scopeTitle: "Zakres usług przeglądu instalacji elektrycznych",
  scopeItems: [
    "Oględziny rozdzielnicy",
    "Pomiar rezystancji izolacji",
    "Ciągłość przewodów ochronnych PE",
    "Impedancja pętli zwarcia",
    "Test wyłączników RCD",
    "Ocena zabezpieczeń",
    "Kontrola gniazd i łączników",
    "Weryfikacja oznaczeń obwodów",
    "Protokół z pomiarami",
  ],
  seo: {
    title: "Przegląd instalacji elektrycznej co 5 lat: cena, zakres, protokół",
    description:
      "Przegląd instalacji elektrycznej co 5 lat dla domu, mieszkania i lokalu. Sprawdź cenę, zakres pomiarów, obowiązek z art. 62 i to, co zawiera protokół.",
  },
};
